import {Injectable} from '@angular/core';
import {GeneralService} from './general.service';
import {HttpClient} from '@angular/common/http';
import {UserRight} from '../interfaces/userRight';
import {UserRightGroup} from '../interfaces/userRightGroup';
import {UserRightGroupUi} from '../interfaces/userRightGroupUi';
import {UserRightService} from './user-right.service';
import {map} from 'rxjs/operators';
import {sortBy} from 'lodash-es';
import {ServiceAnswer} from '../../shared/service-answer';
import {GroupService} from './group.service';
import {UiSettingsService} from '../../shared/ui-settings.service';
import {ServiceTools} from './serviceTools';
import {Group} from '../interfaces/group';
import { MessageService } from '../../shared/message.service';
import { SuperuserServiceModule } from '../superuser-service.module';

@Injectable({
  providedIn: SuperuserServiceModule
})
export class UserRightGroupService extends GeneralService<UserRightGroup> {

  constructor(http: HttpClient,
              private messageService: MessageService,
              private userRightService: UserRightService,
              private groupService: GroupService,
              uiSettingService: UiSettingsService) {
    super(http, uiSettingService, 'userrightgroups', 'userrightgroup');
  }

  /**
   * Adds user rights to group.
   * @param {number} groupId The id of the group for which the rights are added.
   * @param {UserRightGroupUi[]} userRightGroupUis Array of rights.
   * @returns {Promise<ServiceAnswer>} When done.
   */
  public async saveRightsOfGroupUis(groupId: number, userRightGroupUis: UserRightGroupUi[]): Promise<ServiceAnswer<any>> {
    const rightUserRightAdds: RightUserRightAdd[] = [];
    for (const userRightGroupUi of userRightGroupUis) {
      if (userRightGroupUi.isChanged) {
        const rightUserRightAdd: RightUserRightAdd = {
          userRightId: userRightGroupUi.userRightId,
          types: []
        };
        rightUserRightAdds.push(rightUserRightAdd);
        if (userRightGroupUi.isBackReadChanged) {
          rightUserRightAdd.types.push({type: 'rb', active: userRightGroupUi.backRead});
        }
        if (userRightGroupUi.isBackWriteChanged) {
          rightUserRightAdd.types.push({type: 'wb', active: userRightGroupUi.backWrite});
        }
        if (userRightGroupUi.isFrontReadChanged) {
          rightUserRightAdd.types.push({type: 'rf', active: userRightGroupUi.frontRead});
        }
        if (userRightGroupUi.isFrontWriteChanged) {
          rightUserRightAdd.types.push({type: 'wf', active: userRightGroupUi.frontWrite});
        }
      }
    }

    const serviceAnswer: ServiceAnswer<any> = {isOk: null, serviceAnswer: []};
    const urlSets: string[] = this.getUrlFromSelectedContext(null, null, true);
    if (urlSets.length === 1) {
      return await this.saveRightsOfGroupUisSingle(urlSets[0], groupId, rightUserRightAdds);
    } else {
      for (const url of urlSets) {
        serviceAnswer.serviceAnswer.push(await this.saveRightsOfGroupUisSingle(url, groupId, rightUserRightAdds));
      }
      serviceAnswer.isOk = !serviceAnswer.serviceAnswer.some((serviceAnswerInner: ServiceAnswer<any>) => !serviceAnswerInner.isOk);
    }
    return serviceAnswer;
  }

  /**
   * Updates rights of group.
   * @param baseUrl The url to which the update is sent.
   * @param groupId The group The group whose rights are updated.
   * @param rightUserRightAdds The rights to be updated.
   */
  private async saveRightsOfGroupUisSingle(
    baseUrl: string,
    groupId: number,
    rightUserRightAdds: RightUserRightAdd[]): Promise<ServiceAnswer<any>> {
    const serviceAnswer: ServiceAnswer<any> = {isOk: null};
    try {
      const entityObject = await this.http.put(
        `${baseUrl}groups/${groupId}/userright`,
        rightUserRightAdds,
        {
        })
        .pipe(
          map((value: any) => {
            return JSON.stringify(value);
          }))
        .toPromise();
      serviceAnswer.isOk = true;
      serviceAnswer.data = JSON.parse(entityObject);
      return serviceAnswer;
    } catch (error) {
      console.error(`Error when adding user right for group ${groupId}: ${JSON.stringify(error)}`);
      serviceAnswer.isOk = false;
      serviceAnswer.errorCode = error.status;
      serviceAnswer.errorMessage = error.message + '. Answer body: ' + JSON.stringify(error.error);
      return serviceAnswer;
    }
  }

  /**
   * Sets the given user right active for groups.
   * @param {number} userRightId The id of the user right.
   * @param {UserRightGroupUi[]} userRightGroupUis Array of rights.
   * @returns {Promise<ServiceAnswer>} When done.
   */
  public async saveGroupsOfRightUis(userRightId: number, userRightGroupUis: UserRightGroupUi[]): Promise<ServiceAnswer<any>> {
    const rightUserRightAdds: GroupUserRightAdd[] = [];
    for (const userRightGroupUi of userRightGroupUis) {
      if (userRightGroupUi.isChanged) {
        const rightUserRightAdd: GroupUserRightAdd = {
          groupId: userRightGroupUi.groupId,
          types: []
        };
        rightUserRightAdds.push(rightUserRightAdd);
        if (userRightGroupUi.isBackReadChanged) {
          rightUserRightAdd.types.push({type: 'rb', active: userRightGroupUi.backRead});
        }
        if (userRightGroupUi.isBackWriteChanged) {
          rightUserRightAdd.types.push({type: 'wb', active: userRightGroupUi.backWrite});
        }
        if (userRightGroupUi.isFrontReadChanged) {
          rightUserRightAdd.types.push({type: 'rf', active: userRightGroupUi.frontRead});
        }
        if (userRightGroupUi.isFrontWriteChanged) {
          rightUserRightAdd.types.push({type: 'wf', active: userRightGroupUi.frontWrite});
        }
      }
    }

    const serviceAnswer: ServiceAnswer<any>= {isOk: null, serviceAnswer: []};
    const urlSets: string[] = this.getUrlFromSelectedContext(null, null, true);
    if (urlSets.length === 1) {
      return await this.saveGroupsOfRightsUisSingle(urlSets[0], userRightId, rightUserRightAdds);
    } else {
      for (const url of urlSets) {
        serviceAnswer.serviceAnswer.push(await this.saveGroupsOfRightsUisSingle(url, userRightId, rightUserRightAdds));
      }
      serviceAnswer.isOk = !serviceAnswer.serviceAnswer.some((serviceAnswerInner: ServiceAnswer<any>) => !serviceAnswerInner.isOk);
    }
    return serviceAnswer;
  }

  /**
   * Sets the given user right active for groups in single server.
   * @param {number} userRightId The id of the user right.
   * @param {UserRightGroupUi[]} userRightGroupUis Array of rights.
   * @returns {Promise<ServiceAnswer>} When done.
   */
  private async saveGroupsOfRightsUisSingle(
    baseUrl: string,
    userRightId: number,
    rightUserRightAdds: GroupUserRightAdd[]): Promise<ServiceAnswer<any>> {

    const serviceAnswer: ServiceAnswer<any> = {isOk: null};
    try {
      const entityObject = await this.http.put(
        `${baseUrl}userrights/${userRightId}/group`,
        rightUserRightAdds,
        {
        })
        .pipe(
          map((value: any) => {
            return JSON.stringify(value);
          }))
        .toPromise();
      serviceAnswer.isOk = true;
      serviceAnswer.data = JSON.parse(entityObject);
      return serviceAnswer;
    } catch (error) {
      console.error(`Error when adding/removing user right ${userRightId} to/from groups: ${JSON.stringify(error)}`);
      serviceAnswer.isOk = false;
      serviceAnswer.errorCode = error.status;
      serviceAnswer.errorMessage = error.message + '. Answer body: ' + JSON.stringify(error.error);
      return serviceAnswer;

    }
  }

  /**
   * Returns array of objects that contains the information of UserRightGroup object. The objects to be returned are
   * meant to be used in UI. Each of them contain rightId, groupId and booleans of different rights.
   * @param {number} groupId The id of the group for which the rights are returned.
   * @returns {Promise<UserRightGroupUi[]>} An array of values.
   */
  public async getRightsOfGroupUis(groupId: number): Promise<UserRightGroupUi[]> {
    const userRights: UserRight[] = ServiceTools.getEntitiesFromFirstContext(
      await this.userRightService.getEntities(),
      this.messageService,
      'user rights'
    );
    const userRightGroups: UserRightGroup[] = await this.getUserGroupRightOfGroup(groupId);

    const group: Group = ServiceTools.getEntityFromFirstContext(
      await this.groupService.getEntity(groupId),
      this.messageService,
      'group'
    );

    // Create maps for userRightGroups. The key is groupId and value is a set of UserRightGroups.
    const mapUserRightGroupsByUserRight: Map<number, Set<UserRightGroup>> = new Map<number, Set<UserRightGroup>>();
    for (const userRightGroup of userRightGroups) {
      if (!mapUserRightGroupsByUserRight.has(userRightGroup.userRightId)) {
        mapUserRightGroupsByUserRight.set(userRightGroup.userRightId, new Set<UserRightGroup>());
      }
      mapUserRightGroupsByUserRight.get(userRightGroup.userRightId).add(userRightGroup);
    }

    // Create UserRightGroupUi from UserRights and UserRightGroups
    const userRightGroupUis: UserRightGroupUi[] = [];
    for (const userRight of userRights) {
      const userRightGroupUi: UserRightGroupUi = new UserRightGroupUi();
      userRightGroupUi.backRead = false;
      userRightGroupUi.backWrite = false;
      userRightGroupUi.frontRead = false;
      userRightGroupUi.frontWrite = false;
      userRightGroupUi.groupId = groupId;
      userRightGroupUi.groupName = group.name;
      userRightGroupUi.userRightId = userRight.id;
      userRightGroupUi.userRightName = userRight.name;
      userRightGroupUi.userRightTarget = userRight.target;

      // Go trough UserRightGroups in the set to get the rights
      if (mapUserRightGroupsByUserRight.get(userRight.id)) {
        mapUserRightGroupsByUserRight.get(userRight.id).forEach((userRightGroup: UserRightGroup) => {
          if (userRightGroup.type.toLowerCase().includes('b')) {
            if (userRightGroup.type.toLowerCase().includes('w')) {
              userRightGroupUi.backWrite = true;
            }
            if (userRightGroup.type.toLowerCase().includes('r')) {
              userRightGroupUi.backRead = true;
            }
          }
          if (userRightGroup.type.toLowerCase().includes('f')) {
            if (userRightGroup.type.toLowerCase().includes('w')) {
              userRightGroupUi.frontWrite = true;
            }
            if (userRightGroup.type.toLowerCase().includes('r')) {
              userRightGroupUi.frontRead = true;
            }
          }
        });
      }
      userRightGroupUis.push(userRightGroupUi);
    }
    // sort by name, case insensitive, but tie-break with case to ensure stability
    return sortBy(userRightGroupUis, [el => el.userRightName.toLowerCase(), 'userRightName']);
  }

  getUserGroupRightOfGroup(groupId: number): Promise<UserRightGroup[]> {
    return this.http.get<UserRightGroup[]>(
      // `${this.entitiesUrl}?groupId=${groupId}`,
      super.getUrlFromSelectedContext(null, [{key: 'groupId', value: '' + groupId}])[0],
      {
      }).toPromise();
  }

  getUserGroupRightOfRight(userRightId: number): Promise<UserRightGroup[]> {
    return this.http.get<UserRightGroup[]>(
      // `${this.entitiesUrl}?userRightId=${userRightId}`,
      super.getUrlFromSelectedContext(null, [{key: 'userRightId', value: '' + userRightId}])[0],
      {
      }).toPromise();
  }


  /**
   * Returns array of objects that contains the information of UserRightGroup object. The objects to be returned are
   * meant to be used in UI. Each of them contain rightId, groupId and booleans of different rights.
   * @param {number} groupId The id of the group for which the rights are returned.
   * @returns {Promise<UserRightGroupUi[]>} An array of values.
   */
  public async getGroupsOfRightUis(userRightId: number): Promise<UserRightGroupUi[]> {
    const userRight: UserRight = ServiceTools.getEntityFromFirstContext(
      await this.userRightService.getEntity(userRightId),
      this.messageService,
      'user right');
    const groups: Group[] = ServiceTools.getEntitiesFromFirstContext(
      await this.groupService.getEntities(),
      this.messageService,
      'groups'
    );
    // const userRightGroups: UserRightGroup[] = await this.getEntities();
    const userRightGroups: UserRightGroup[] = await this.getUserGroupRightOfRight(userRightId);

    // Create maps for userRightGroups. The key is groupId and value is a set of UserRightGroups.
    const mapUserRightGroupsByGroup: Map<number, Set<UserRightGroup>> = new Map<number, Set<UserRightGroup>>();
    for (const userRightGroup of userRightGroups) {
      if (!mapUserRightGroupsByGroup.has(userRightGroup.groupId)) {
        mapUserRightGroupsByGroup.set(userRightGroup.groupId, new Set<UserRightGroup>());
      }
      mapUserRightGroupsByGroup.get(userRightGroup.groupId).add(userRightGroup);
    }

    // Create UserRightGroupUi from UserRights and UserRightGroups
    const userRightGroupUis: UserRightGroupUi[] = [];
    for (const group of groups) {
      const userRightGroupUi: UserRightGroupUi = new UserRightGroupUi();
      userRightGroupUi.backRead = false;
      userRightGroupUi.backWrite = false;
      userRightGroupUi.frontRead = false;
      userRightGroupUi.frontWrite = false;
      userRightGroupUi.groupId = group.id;
      userRightGroupUi.groupName = group.name;
      userRightGroupUi.userRightId = userRight.id;
      userRightGroupUi.userRightName = userRight.name;
      userRightGroupUi.userRightTarget = userRight.target;

      // Go trough UserRightGroups in the set to get the rights
      if (mapUserRightGroupsByGroup.get(group.id)) {
        mapUserRightGroupsByGroup.get(group.id).forEach((userRightGroup: UserRightGroup) => {
          if (userRightGroup.type.toLowerCase().includes('b')) {
            if (userRightGroup.type.toLowerCase().includes('w')) {
              userRightGroupUi.backWrite = true;
            }
            if (userRightGroup.type.toLowerCase().includes('r')) {
              userRightGroupUi.backRead = true;
            }
          }
          if (userRightGroup.type.toLowerCase().includes('f')) {
            if (userRightGroup.type.toLowerCase().includes('w')) {
              userRightGroupUi.frontWrite = true;
            }
            if (userRightGroup.type.toLowerCase().includes('r')) {
              userRightGroupUi.frontRead = true;
            }
          }
        });
      }
      userRightGroupUis.push(userRightGroupUi);
    }
    return userRightGroupUis;
  }
}

interface RightUserRightAdd {
  userRightId: number;
  types: { type: string, active: boolean }[];
}


interface GroupUserRightAdd {
  groupId: number;
  types: { type: string, active: boolean }[];
}

